// src/views/JobManagementView.js

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, onSnapshot, deleteDoc, doc, orderBy, updateDoc, getDoc, getDocs, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Plus, LayoutGrid, List, XCircle, Search, Radio, CheckCircle, Clock, MapPin, Timer, ChevronDown, ChevronUp, Users, CheckSquare, Square, FileText, Send, RotateCcw } from 'lucide-react';
import { startOfWeek, endOfWeek, startOfDay, endOfDay, addDays, startOfMonth, endOfMonth, addMonths, parseISO, format } from 'date-fns';
import { projectRecurringOccurrences } from '../utils/recurrence'; // r1-rec-jobslist-expand-d7b4e1f9
import JobTable from '../components/crm/JobTable'; 
import JobKanbanBoard from '../components/crm/JobKanbanBoard'; 
import ConfirmationModal from '../components/common/ConfirmationModal';
import { can, PERMISSIONS, canBatchInvoice } from '../utils/permissions'; // canBatchInvoice: s8portal-4e7b2c95
import NoteInclusionPicker from '../components/crm/NoteInclusionPicker'; // r1-3f-jmv-import-4e9b2a71
import { fetchCustomerNoteGroups, buildNoteInclusions } from '../utils/noteInclusions'; // r1-3f-jmv-import-4e9b2a71
import { getJobArrivalWindowShortLabel } from '../utils/arrivalWindows'; // aw-jmv-708192a3 // aw-seam4
import { matchesJobSearch } from '../utils/jobSearch'; // calpol-9c4e7b23
import { readChecklistGate, explainChecklistRefusal } from '../utils/checklistClient'; // chklstweb-3a7f1c58
import { formatJobNumber } from '../utils/formatters'; // chklstweb-3a7f1c58
import { compareJobNumbers } from '../utils/jobNumberSort'; // jobnumsort-6a1f3d85

const datePresets = [
    // jobsall-9e4c2f18 — "All" clears the date window entirely rather than widening it,
    // so it has to be a legal value in VALID_JOBS_PRESETS below too, or the saved
    // preference is rejected on read and silently reverts to This Week.
    { value: 'all', label: 'All' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'thisweek', label: 'This Week' },
    { value: 'nextweek', label: 'Next Week' },
    { value: 'thismonth', label: 'This Month' },
    { value: 'nextmonth', label: 'Next Month' },
    { value: 'custom', label: 'Custom' },
];

// ─────────────────────────────────────────────────────────────────────────────
// JOBS VIEW-STATE PERSISTENCE (marker 7c3af9d1)
// Persist the Jobs screen's view config per-user in localStorage so it survives
// navigating away and back. Client-only, matching the `spatchy_*` convention used
// by CustomerTable.js / CustomerManagementView.js (no Firestore, no cross-device).
// ─────────────────────────────────────────────────────────────────────────────
const JOBS_PREFS_KEY_PREFIX = 'spatchy_jobs_view_prefs_';

// Whitelists used to validate restored values field-by-field. An invalid field
// falls back to its own default without discarding the others.
const VALID_JOBS_VIEW_MODES = ['board', 'table', 'dispatch'];
const VALID_JOBS_SORT_KEYS = ['jobNumber', 'title', 'customerName', 'startAt', 'assignedToNames', 'status']; // keys the JobTable headers can emit
const VALID_JOBS_STATUS_FILTERS = ['All', 'Pending', 'Scheduled', 'In Progress', 'Completed', 'NeedsInvoicing'];
const VALID_JOBS_PRESETS = ['all', 'today', 'tomorrow', 'thisweek', 'nextweek', 'thismonth', 'nextmonth', 'custom']; // jobsall-9e4c2f18 — 'all' must be here or the stored pref reverts

const JOBS_PREFS_DEFAULTS = {
    viewMode: 'board',
    sortConfig: { key: 'startAt', direction: 'descending' },
    statusFilter: 'All',
    selectedPreset: 'thisweek',
    startDate: null,
    endDate: null,
};

// Read + validate persisted prefs. Returns a fully-defaulted config object; never
// throws and never clears the key on a bad read (marker 7c3af9d1).
const readJobsViewPrefs = (profileId) => {
    const result = { ...JOBS_PREFS_DEFAULTS, sortConfig: { ...JOBS_PREFS_DEFAULTS.sortConfig } };
    try {
        const raw = localStorage.getItem(JOBS_PREFS_KEY_PREFIX + profileId);
        if (!raw) return result;
        const p = JSON.parse(raw);
        if (!p || p.v !== 1) return result;

        if (VALID_JOBS_VIEW_MODES.includes(p.viewMode)) result.viewMode = p.viewMode;

        if (p.sortConfig
            && VALID_JOBS_SORT_KEYS.includes(p.sortConfig.key)
            && ['ascending', 'descending'].includes(p.sortConfig.direction)) {
            result.sortConfig = { key: p.sortConfig.key, direction: p.sortConfig.direction };
        }

        if (VALID_JOBS_STATUS_FILTERS.includes(p.statusFilter)) result.statusFilter = p.statusFilter;

        if (VALID_JOBS_PRESETS.includes(p.selectedPreset)) result.selectedPreset = p.selectedPreset;

        // Custom dates: restore only if the preset is 'custom', savedAt is within the
        // last 24h, and both date strings parse. Otherwise force 'thisweek' and leave
        // startDate/endDate null so the preset effect recomputes a fresh window.
        if (result.selectedPreset === 'custom') {
            const savedAt = typeof p.savedAt === 'number' ? p.savedAt : 0;
            const age = Date.now() - savedAt;
            let datesOk = false;
            if (savedAt > 0 && age >= 0 && age < 24 * 60 * 60 * 1000
                && typeof p.customStartDate === 'string' && typeof p.customEndDate === 'string') {
                try {
                    const s = parseISO(p.customStartDate);
                    const e = parseISO(p.customEndDate);
                    if (!isNaN(s.getTime()) && !isNaN(e.getTime())) datesOk = true;
                } catch { datesOk = false; }
            }
            if (datesOk) {
                result.startDate = p.customStartDate;
                result.endDate = p.customEndDate;
            } else {
                result.selectedPreset = 'thisweek';
                result.startDate = null;
                result.endDate = null;
            }
        }

        return result;
    } catch {
        return { ...JOBS_PREFS_DEFAULTS, sortConfig: { ...JOBS_PREFS_DEFAULTS.sortConfig } };
    }
};


// ─────────────────────────────────────────────────────────────────────────────
// DISPATCH VIEW
// Today's jobs grouped by crew in optimized route order.
// Reads savedRoutes for today's stop sequence; falls back to startAt order.
// Real-time — reflects status changes from field workers instantly.
// ─────────────────────────────────────────────────────────────────────────────
const CREW_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#ec4899'];

const statusConfig = {
    'Scheduled':   { dot: 'bg-gray-400',            label: 'Scheduled',   pill: 'bg-gray-100 text-gray-600'  },
    'Pending':     { dot: 'bg-orange-400',           label: 'Pending',     pill: 'bg-orange-100 text-orange-700' },
    'In Progress': { dot: 'bg-blue-500 animate-pulse', label: 'In Progress', pill: 'bg-blue-100 text-blue-700'  },
    'Completed':   { dot: 'bg-green-500',            label: 'Completed',   pill: 'bg-green-100 text-green-700' },
    'Canceled':    { dot: 'bg-red-400',              label: 'Canceled',    pill: 'bg-red-100 text-red-600'    },
};

const DispatchView = ({ jobs, currentUserProfile, onRequestJobStart }) => {
    const orgId = currentUserProfile?.organizationId;
    const [routeGroups, setRouteGroups] = useState([]);
    const [savedRoutes, setSavedRoutes] = useState({}); // crewId → [orderedJobIds]
    const [expanded, setExpanded] = useState({}); // crewId → bool (mobile accordion)
    const [isLoadingRoutes, setIsLoadingRoutes] = useState(true);
    const todayStr = format(new Date(), 'yyyy-MM-dd');

    // Fetch route groups
    useEffect(() => {
        if (!orgId) return;
        return onSnapshot(
            query(collection(db, `organizations/${orgId}/routeGroups`), orderBy('name')),
            snap => setRouteGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
    }, [orgId]);

    // Fetch today's saved routes (optimized stop order)
    useEffect(() => {
        if (!orgId || routeGroups.length === 0) return;
        setIsLoadingRoutes(true);
        const fetches = routeGroups.map(g =>
            getDoc(doc(db, `organizations/${orgId}/savedRoutes`, `${todayStr}_${g.id}`))
                .then(snap => ({ crewId: g.id, snap }))
                .catch(() => ({ crewId: g.id, snap: null }))
        );
        Promise.all(fetches).then(results => {
            const map = {};
            results.forEach(({ crewId, snap }) => {
                if (snap?.exists()) {
                    const data = snap.data();
                    // crewJobs is { [crewId]: [{ jobId, stopOrder, ... }] }
                    const ordered = (data.crewJobs?.[crewId] || [])
                        .sort((a, b) => (a.stopOrder || 0) - (b.stopOrder || 0));
                    map[crewId] = ordered.map(j => j.jobId);
                }
            });
            setSavedRoutes(map);
            setIsLoadingRoutes(false);
        });
    }, [orgId, routeGroups, todayStr]);

    // Derive duration label from endAt - startAt
    const getDuration = (job) => {
        const start = job.startAt?.toDate ? job.startAt.toDate() : (job.startAt instanceof Date ? job.startAt : null);
        const end   = job.endAt?.toDate   ? job.endAt.toDate()   : (job.endAt   instanceof Date ? job.endAt   : null);
        if (!start || !end) return null;
        const mins = Math.round((end - start) / 60000);
        if (mins <= 0) return null;
        const h = Math.floor(mins / 60), m = mins % 60;
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
    };

    // Order jobs for a crew: saved route order first, then remaining by startAt
    const getOrderedJobsForCrew = (crewId) => {
        const crewJobs = jobs.filter(j => {
            if (j.status === 'Canceled') return false;
            return j.routeGroupId === crewId ||
                   j.assignedGroupMemberIds?.includes(crewId) ||
                   j.assignedTo?.some(id => (routeGroups.find(g => g.id === crewId)?.memberIds || []).includes(id));
        });

        const orderedIds = savedRoutes[crewId] || [];
        if (orderedIds.length === 0) {
            // No saved route — sort by startAt
            return crewJobs.sort((a, b) => {
                const ta = a.startAt?.toDate ? a.startAt.toDate() : new Date(0);
                const tb = b.startAt?.toDate ? b.startAt.toDate() : new Date(0);
                return ta - tb;
            });
        }

        const byId = Object.fromEntries(crewJobs.map(j => [j.id, j]));
        const ordered = orderedIds.map(id => byId[id]).filter(Boolean);
        // Append any jobs not in the saved route (e.g. last-minute additions)
        const orderedIdSet = new Set(orderedIds);
        const extras = crewJobs
            .filter(j => !orderedIdSet.has(j.id))
            .sort((a, b) => {
                const ta = a.startAt?.toDate ? a.startAt.toDate() : new Date(0);
                const tb = b.startAt?.toDate ? b.startAt.toDate() : new Date(0);
                return ta - tb;
            });
        return [...ordered, ...extras];
    };

    // Summary stats for a crew's jobs
    const getCrewStats = (crewJobs) => {
        const total     = crewJobs.length;
        const completed = crewJobs.filter(j => j.status === 'Completed').length;
        const inProg    = crewJobs.filter(j => j.status === 'In Progress').length;
        return { total, completed, inProg, remaining: total - completed };
    };

    // Groups with at least one job today
    const activeGroups = routeGroups.filter(g => getOrderedJobsForCrew(g.id).length > 0);
    // Unassigned jobs (no routeGroupId or not matched to any group)
    const assignedJobIds = new Set(activeGroups.flatMap(g => getOrderedJobsForCrew(g.id).map(j => j.id)));
    const unassigned = jobs.filter(j => j.status !== 'Canceled' && !assignedJobIds.has(j.id));

    if (isLoadingRoutes) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm gap-2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                Loading today's dispatch…
            </div>
        );
    }

    if (activeGroups.length === 0 && unassigned.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Radio size={28} className="text-gray-400" />
                </div>
                <p className="text-gray-700 font-semibold text-base mb-1">No jobs scheduled today</p>
                <p className="text-gray-400 text-sm">Jobs will appear here when scheduled for today.</p>
            </div>
        );
    }

    const DispatchJobCard = ({ job, stopNumber, crewColor }) => {
        const cfg = statusConfig[job.status] || statusConfig['Scheduled'];
        const duration = getDuration(job);
        const isComplete = job.status === 'Completed';
        const startLabel = job.schedulingMode === 'window'
            ? getJobArrivalWindowShortLabel(job)
            : (job.startAt?.toDate ? format(job.startAt.toDate(), 'h:mm a') : null);

        return (
            <div
                onClick={() => onRequestJobStart && onRequestJobStart(job)}
                className={`flex items-start gap-3 p-3.5 rounded-xl border bg-white cursor-pointer transition-all hover:shadow-md hover:border-blue-200 active:scale-[0.98] ${
                    isComplete ? 'opacity-55' : ''
                }`}
            >
                {/* Stop number */}
                <div className="relative flex-shrink-0">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                         style={{ backgroundColor: crewColor }}>
                        {stopNumber}
                    </div>
                    <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${cfg.dot}`} />
                </div>

                {/* Job info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-semibold truncate ${isComplete ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                            {job.title}
                        </p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.pill}`}>
                            {cfg.label}
                        </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{job.clientName || job.customerDetails?.name}</p>
                    {job.address && (
                        <p className="text-xs text-gray-400 truncate flex items-center gap-1 mt-1">
                            <MapPin size={9} className="flex-shrink-0" />{job.address}
                        </p>
                    )}
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {startLabel && (
                            <span className="text-[10px] text-blue-600 flex items-center gap-0.5">
                                <Clock size={9} />{startLabel}
                            </span>
                        )}
                        {duration && (
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                <Timer size={9} />{duration}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="mt-4">
            {/* Desktop: column per crew | Mobile: accordion */}
            <div className="hidden lg:grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(activeGroups.length + (unassigned.length > 0 ? 1 : 0), 4)}, 1fr)` }}>
                {activeGroups.map((group, gIdx) => {
                    const crewColor = CREW_COLORS[gIdx % CREW_COLORS.length];
                    const crewJobs  = getOrderedJobsForCrew(group.id);
                    const stats     = getCrewStats(crewJobs);
                    return (
                        <div key={group.id} className="flex flex-col bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
                            {/* Crew header */}
                            <div className="px-4 py-3 border-b border-gray-200" style={{ borderLeftWidth: 4, borderLeftColor: crewColor }}>
                                <p className="text-sm font-bold text-gray-800">{group.name}</p>
                                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                    <span>{stats.completed}/{stats.total} done</span>
                                    {stats.inProg > 0 && (
                                        <span className="text-blue-600 font-medium flex items-center gap-0.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse inline-block" /> {stats.inProg} in progress
                                        </span>
                                    )}
                                </div>
                                {/* Progress bar */}
                                <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
                                    <div className="h-1 rounded-full bg-green-500 transition-all"
                                         style={{ width: stats.total > 0 ? `${(stats.completed / stats.total) * 100}%` : '0%' }} />
                                </div>
                            </div>
                            {/* Jobs */}
                            <div className="p-3 space-y-2 flex-1 overflow-y-auto max-h-[calc(100vh-280px)]">
                                {crewJobs.map((job, idx) => (
                                    <DispatchJobCard key={job.id} job={job} stopNumber={idx + 1} crewColor={crewColor} />
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* Unassigned column */}
                {unassigned.length > 0 && (
                    <div className="flex flex-col bg-gray-50 rounded-2xl border border-dashed border-gray-300 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200">
                            <p className="text-sm font-bold text-gray-500">Unassigned</p>
                            <p className="text-xs text-gray-400 mt-0.5">{unassigned.length} job{unassigned.length !== 1 ? 's' : ''} not on a crew route</p>
                        </div>
                        <div className="p-3 space-y-2 flex-1">
                            {unassigned.map((job, idx) => (
                                <DispatchJobCard key={job.id} job={job} stopNumber="—" crewColor="#9ca3af" />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile: accordion */}
            <div className="lg:hidden space-y-3">
                {activeGroups.map((group, gIdx) => {
                    const crewColor = CREW_COLORS[gIdx % CREW_COLORS.length];
                    const crewJobs  = getOrderedJobsForCrew(group.id);
                    const stats     = getCrewStats(crewJobs);
                    const isOpen    = expanded[group.id] !== false; // default open
                    return (
                        <div key={group.id} className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                            <button
                                type="button"
                                onClick={() => setExpanded(p => ({ ...p, [group.id]: !isOpen }))}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left"
                                style={{ borderLeftWidth: 4, borderLeftColor: crewColor }}
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-gray-800">{group.name}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <p className="text-xs text-gray-500">{stats.completed}/{stats.total} done</p>
                                        {stats.inProg > 0 && (
                                            <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse inline-block" />{stats.inProg} in progress
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1.5 w-full bg-gray-200 rounded-full h-1">
                                        <div className="h-1 rounded-full bg-green-500 transition-all"
                                             style={{ width: stats.total > 0 ? `${(stats.completed / stats.total) * 100}%` : '0%' }} />
                                    </div>
                                </div>
                                {isOpen ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
                            </button>
                            {isOpen && (
                                <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
                                    <div className="pt-2 space-y-2">
                                        {crewJobs.map((job, idx) => (
                                            <DispatchJobCard key={job.id} job={job} stopNumber={idx + 1} crewColor={crewColor} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                {unassigned.length > 0 && (
                    <div className="rounded-2xl border border-dashed border-gray-300 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200">
                            <p className="text-sm font-bold text-gray-500">Unassigned ({unassigned.length})</p>
                        </div>
                        <div className="p-3 space-y-2">
                            {unassigned.map(job => (
                                <DispatchJobCard key={job.id} job={job} stopNumber="—" crewColor="#9ca3af" />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const JobManagementView = ({ currentUserProfile, openJobEditorModal, onRequestJobStart, onGenerateInvoice, techJobCreationEnabled, orgTimeZone, visitWallClockEnabled }) => { // orgTimeZone: orgtime-7c2e4b19; visitWallClockEnabled: tzhour-6a3d9c58
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // Restore persisted view state once, in a lazy initializer, so there's no flash
    // of defaults before an effect runs (marker 7c3af9d1). currentUserProfile is
    // guaranteed non-null with a populated .id here (ProtectedCrmLayout gates render).
    const [jobsViewPrefs] = useState(() => readJobsViewPrefs(currentUserProfile.id));
    const [viewMode, setViewMode] = useState(jobsViewPrefs.viewMode);
    // Auto-switch to today's preset when entering dispatch mode
    const handleSetViewMode = (mode) => {
        setViewMode(mode);
        if (mode === 'dispatch') setSelectedPreset('today');
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [jobToDelete, setJobToDelete] = useState(null);
    const [sortConfig, setSortConfig] = useState(jobsViewPrefs.sortConfig); // restored (marker 7c3af9d1)
    const [resources, setResources] = useState([]);

    // --- BATCH STATE ---
    const [batchMode, setBatchMode] = useState(false);
    const [selectedJobIds, setSelectedJobIds] = useState(new Set());
    const [statusFilter, setStatusFilter] = useState(jobsViewPrefs.statusFilter); // restored (marker 7c3af9d1)
    const [batchCompleteModal, setBatchCompleteModal] = useState(false);
    const [batchInvoiceModal, setBatchInvoiceModal] = useState(false);
    // r1-3f-jmv-state-6d3c9f82 — note-inclusion picker for the batch invoice modal.
    const [noteGroups, setNoteGroups] = useState([]);                            // customer-facing notes per selected job
    const [excludedNoteIds, setExcludedNoteIds] = useState(new Set());           // unticked note ids (ticked by default)
    const [noteFetchFailed, setNoteFetchFailed] = useState(false);               // true → degrade toward including everything
    const [sendImmediately, setSendImmediately] = useState(false);
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchResults, setBatchResults] = useState(null);
    const [multiCrewWarning, setMultiCrewWarning] = useState(null); // { affected: [{job, pendingCrews}], cleanIds: [] }

    const [startDate, setStartDate] = useState(jobsViewPrefs.startDate); // restored (marker 7c3af9d1)
    const [endDate, setEndDate] = useState(jobsViewPrefs.endDate);       // restored (marker 7c3af9d1)
    const [selectedPreset, setSelectedPreset] = useState(jobsViewPrefs.selectedPreset); // restored (marker 7c3af9d1)

    // --- DEEP LINKING ---
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const filterParam = queryParams.get('filter'); 

    const jobStatuses = ['Pending', 'Scheduled', 'In Progress', 'Completed', 'Canceled'];

    // --- 1. HANDLE DEEP LINK FILTER SETUP ---
    useEffect(() => {
        if (filterParam === 'RequiresInvoicing') {
            setViewMode('table');
            setStartDate(null); 
            setEndDate(null);
            setSelectedPreset(''); 
        } else if (filterParam === 'NeedsConfirmation') {
            setViewMode('board');
            setStartDate(null);
            setEndDate(null);
            setSelectedPreset('');
        }
    }, [filterParam]);

    // --- 2. DATA FETCHING (JOBS & RECURRING) ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
    
        const jobsRef = collection(db, `organizations/${orgId}/jobs`);
        const recurringJobsRef = collection(db, `organizations/${orgId}/recurringJobs`);
    
        let latestJobs = [];
        let latestRecurring = [];

        const unsubJobs = onSnapshot(
            query(jobsRef, orderBy('startAt', 'desc')),
            (snap) => {
                // r1-rec-subid-docwins-c3a8e5f1
                // Doc id must win over any stale `id` FIELD inside the doc data.
                // Some job docs carry an `id` field (legacy / recurring-exception
                // writes); with `{ id: d.id, ...d.data() }` the spread clobbered
                // d.id, so job.id became the field value — causing template-id
                // collisions and duplicate React keys in JobTable. Spread first,
                // then set id from d.id last so the Firestore doc id is authoritative.
                latestJobs = snap.docs.map(d => ({ ...d.data(), id: d.id }));
                setJobs([...latestJobs, ...latestRecurring]);
                setIsLoading(false);
            },
            (error) => {
                console.error("Error fetching jobs:", error);
                setIsLoading(false);
            }
        );

        const unsubRecurringJobs = onSnapshot(
            query(recurringJobsRef, orderBy('startAt', 'desc')),
            (snap) => {
                // r1-rec-subid-docwins-c3a8e5f1
                latestRecurring = snap.docs.map(d => ({ ...d.data(), id: d.id }));
                setJobs([...latestJobs, ...latestRecurring]);
            },
            (error) => {
                console.warn("Could not fetch recurring jobs:", error.code);
            }
        );

        return () => {
            unsubJobs();
            unsubRecurringJobs();
        };
    }, [currentUserProfile]);

    // --- 3. DATA FETCHING (RESOURCES) ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const resourcesRef = collection(db, `organizations/${orgId}/resources`);
        const q = query(resourcesRef, orderBy('name'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setResources(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => {
            console.error("Error fetching resources:", error);
        });

        return () => unsubscribe();
    }, [currentUserProfile]);

    // --- 4. DATE PRESET LOGIC ---
    useEffect(() => {
        if (filterParam === 'RequiresInvoicing' || filterParam === 'NeedsConfirmation') return;

        // jobsall-9e4c2f18 — "All" means NO window, so it returns before the switch: the
        // tail of this effect ends in format(startDt, ...), which needs a real Date and
        // would throw on null. The !== null guards are load-bearing, not defensive tidying
        // — this effect lists startDate/endDate in its OWN dependency array (see the deps
        // below), so setting them unconditionally would re-run it on its own writes.
        if (selectedPreset === 'all') {
            if (startDate !== null) setStartDate(null);
            if (endDate !== null) setEndDate(null);
            return;
        }

        const today = new Date();
        let startDt, endDt; 

        switch (selectedPreset) {
            case 'today':
                startDt = startOfDay(today);
                endDt = endOfDay(today);
                break;
            case 'tomorrow':
                const tomorrow = addDays(today, 1);
                startDt = startOfDay(tomorrow);
                endDt = endOfDay(tomorrow);
                break;
            case 'thisweek':
                startDt = startOfWeek(today);
                endDt = endOfWeek(today);
                break;
            case 'nextweek':
                const nextWeekStart = addDays(startOfWeek(today), 7);
                startDt = nextWeekStart;
                endDt = endOfWeek(nextWeekStart);
                break;
            case 'thismonth':
                startDt = startOfMonth(today);
                endDt = endOfMonth(today);
                break;
            case 'nextmonth':
                const nextMonthStart = addMonths(startOfMonth(today), 1);
                startDt = nextMonthStart;
                endDt = endOfMonth(nextMonthStart);
                break;
            case 'custom':
                if (startDate && endDate) {
                    try {
                        startDt = parseISO(startDate); 
                        endDt = parseISO(endDate);
                        startDt.setHours(0,0,0,0);
                        endDt.setHours(23,59,59,999);
                    } catch {
                        console.warn("Invalid custom dates, defaulting to this week.");
                        startDt = startOfWeek(today);
                        endDt = endOfWeek(today);
                    }
                } else {
                    startDt = startOfWeek(today);
                    endDt = endOfWeek(today);
                }
                break;
            default: 
                startDt = startOfWeek(today);
                endDt = endOfWeek(today);
        }

        setStartDate(format(startDt, 'yyyy-MM-dd'));
        setEndDate(format(endDt, 'yyyy-MM-dd'));

    }, [selectedPreset, filterParam, startDate, endDate]);

    // --- 4b. PERSIST VIEW STATE (marker 7c3af9d1) ---
    // Write the current view config to localStorage on change. Fully suppressed
    // whenever a `filter` query param is present so HomeView deep-link state
    // (?filter=NeedsConfirmation / RequiresInvoicing) is never persisted. Custom
    // dates + savedAt are written only when the preset is 'custom'.
    useEffect(() => {
        if (filterParam) return; // never persist deep-link intent
        try {
            const payload = {
                v: 1,
                viewMode,
                sortConfig,
                statusFilter,
                selectedPreset,
            };
            if (selectedPreset === 'custom') {
                payload.customStartDate = startDate;
                payload.customEndDate = endDate;
                payload.savedAt = Date.now();
            }
            localStorage.setItem(JOBS_PREFS_KEY_PREFIX + currentUserProfile.id, JSON.stringify(payload));
        } catch {
            // private mode / quota can throw — the view still works, just no persistence
        }
    }, [viewMode, sortConfig, statusFilter, selectedPreset, startDate, endDate, filterParam, currentUserProfile.id]);

    // --- 5. FILTERING & SORTING ---

    // techinv-8c3f5a21-b — hoisted so the Needs Invoicing count and the date-window
    // branch share ONE definition of "this is a recurring template, not a real job".
    // It used to be declared inside the date-window branch only, which is why the
    // RequiresInvoicing deep-link path above could include a template.
    const isRecurringTemplate = (j) => !!(j.recurrence?.frequency && j.recurrence.frequency !== 'Never');

    // techinv-8c3f5a21-b — one search predicate, used by the main list and by the
    // Needs Invoicing set, so the pill's count can never drift from its rows.
    // calpol-9c4e7b23 — hoisted to utils/jobSearch.js so the calendar's new search
    // box shares it rather than carrying a second copy. Same contract (the caller
    // pre-lowercases the term); it now also strips a leading '#' and searches
    // customerDetails.name, both of which only widen what is found.

    // techinv-8c3f5a21-b — THE Needs Invoicing set: org-wide, NOT date-windowed.
    //
    // This is the whole point of the count. The pill used to filter
    // `filteredAndSortedJobs`, which is scoped to the chosen date range (default
    // "This Week"), so a job completed three weeks ago and still uninvoiced was
    // neither listed nor counted — exactly the job an office needs to be told about.
    // Bypassing the window is the same thing the `/jobs?filter=RequiresInvoicing`
    // deep link already does, so this makes the pill agree with the dashboard tile
    // that links to it.
    //
    // Same predicate as before (`Completed` with no invoiceId); templates and
    // non-Job events excluded; search still applied so the number always matches
    // the rows on screen.
    const needsInvoicingJobs = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return jobs.filter(j =>
            (!j.eventType || j.eventType === 'Job') &&
            !isRecurringTemplate(j) &&
            j.status === 'Completed' &&
            !j.invoiceId &&
            (term === '' || matchesJobSearch(j, term))
        );
    }, [jobs, searchTerm]);

    const filteredAndSortedJobs = useMemo(() => {
        let filteredItems = jobs.filter(j => !j.eventType || j.eventType === 'Job');

        if (filterParam === 'RequiresInvoicing') {
            return filteredItems.filter(j => 
                j.status === 'Completed' && 
                !j.invoiceId 
            );
        }

        if (filterParam === 'NeedsConfirmation') {
            return filteredItems.filter(j => j.status === 'Pending');
        }

        // jobsall-9e4c2f18 — with no date window ("All"), the windowed branch below is
        // skipped and the list would fall through to raw `jobs`, which CONTAINS recurring
        // templates: precisely the bare "-" rows the comment in that branch records having
        // fixed. Exclude them here so "All" cannot reintroduce the bug.
        //
        // Consequence worth stating plainly: "All" shows every job that EXISTS — one-offs
        // and materialised recurring visits alike — but draws no future projected
        // occurrences. Projection needs a bounded window to expand into and "All" has no
        // end date, so there is nothing to bound it with. "All" is complete history, not
        // a forecast.
        //
        // Placed AFTER the two ?filter= early-returns on purpose: those paths keep their
        // existing behaviour (including their own known template quirk), which is not this
        // seam's business to change.
        if (!startDate || !endDate) {
            filteredItems = filteredItems.filter(j => !isRecurringTemplate(j));
        }

        if (startDate && endDate) {
            // r1-rec-jobslist-expand-d7b4e1f9
            // Expand recurring occurrences within the active date window through the
            // shared engine (utils/recurrence.js) — the SAME path CalendarView uses.
            // Previously this branch filtered raw `jobs`, which contains recurring
            // TEMPLATES with no expansion: templates leaked in as bare "-" rows and
            // future occurrences never appeared. Now templates are partitioned out,
            // projected into virtual occurrence rows bounded to the window, and
            // merged back with the real jobs.
            try {
                const filterStart = startOfDay(parseISO(startDate));
                const filterEnd = endOfDay(parseISO(endDate));

                const isTemplate = isRecurringTemplate; // techinv-8c3f5a21-b — hoisted above; one definition
                const toDateSafe = (v) => (v?.toDate ? v.toDate() : (v instanceof Date ? v : null));

                // Real jobs (and materialized recurring instances) — same window
                // check as before, but templates are excluded so they no longer
                // render as raw "-" rows.
                const realInWindow = jobs.filter(job => {
                    if (isTemplate(job)) return false;
                    const jobStart = job.startAt?.toDate();
                    if (!jobStart) return false;
                    return jobStart >= filterStart && jobStart <= filterEnd;
                });

                // Templates -> convert dates to JS Date (this subscription does not
                // convert), then project bounded to the window.
                const templates = jobs.filter(isTemplate).map(t => ({
                    ...t,
                    startAt: toDateSafe(t.startAt),
                    endAt: toDateSafe(t.endAt),
                    recurrence: {
                        ...t.recurrence,
                        endDate: toDateSafe(t.recurrence?.endDate),
                    },
                }));

                // The engine owns dedup now: hand it the raw `jobs` array as
                // realInstances and it builds the series-scoped, day-level key
                // (recurringJobId_yyyy-MM-dd, Canceled instances kept) itself.
                const virtualRows = projectRecurringOccurrences({
                    templates,
                    realInstances: jobs,
                    windowStart: filterStart,
                    windowEnd: filterEnd,
                    tz: orgTimeZone, // orgtime-7c2e4b19 — a series runs through its end date's company day
                    visitWallClockEnabled, // tzhour-6a3d9c58 — projections at the times the server will create
                }).map(({ template, jobStart, jobEnd }) => ({
                    ...template,
                    id: `${template.id}_${jobStart.toISOString()}`,
                    recurringJobId: template.id,
                    startAt: jobStart,
                    endAt: jobEnd,
                    isProjected: true,
                }));

                filteredItems = [...realInWindow, ...virtualRows];
            } catch (e) {
                console.error("Error parsing filter dates:", e);
                filteredItems = [...jobs]; 
            }
        }

        let sortableItems = [...filteredItems]; 
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                // jobnumsort-6a1f3d85 — "Job #" sorts by numeric value (9999 before
                // 10000), ignoring a leading "#"; rows with no number go last both ways.
                if (sortConfig.key === 'jobNumber') {
                    return compareJobNumbers(a.jobNumber, b.jobNumber, sortConfig.direction);
                }

                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                if (['startAt', 'endAt', 'createdAt'].includes(sortConfig.key)) {
                    // r1-rec-sort-datesafe-e2a9c4b7
                    // Virtual recurring rows carry startAt/endAt as JS Dates, while
                    // real jobs carry Firestore Timestamps. A bare ?.toDate() throws
                    // on a Date (no such method). Handle both shapes (and null),
                    // matching the defensive pattern used in JobFormModal.
                    const toSortDate = (v) =>
                        v?.toDate ? v.toDate() : (v instanceof Date ? v : new Date(0));
                    valA = toSortDate(a[sortConfig.key]);
                    valB = toSortDate(b[sortConfig.key]);
                }
                
                if (sortConfig.key === 'customerName') {
                    valA = a.customerDetails?.name || a.clientName || '';
                    valB = b.customerDetails?.name || b.clientName || '';
                }

                if (sortConfig.key === 'assignedToNames') {
                   valA = (a.assignedToNames || []).join(', ');
                   valB = (b.assignedToNames || []).join(', ');
                }

                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        
        if (searchTerm.trim()) {
            // techinv-8c3f5a21-b — shared predicate; see matchesJobSearch above.
            const term = searchTerm.trim().toLowerCase();
            return sortableItems.filter(j => matchesJobSearch(j, term));
        }

        return sortableItems;
    }, [jobs, sortConfig, startDate, endDate, filterParam, searchTerm, orgTimeZone, visitWallClockEnabled]);

    // Whether any selected jobs can still be completed
    const hasCompletableSelected = useMemo(() =>
        [...selectedJobIds].some(id => {
            const job = jobs.find(j => j.id === id);
            return job && !['Completed', 'Canceled'].includes(job.status);
        }), [selectedJobIds, jobs]);

    // Status filter applied only in table view
    const statusFilteredJobs = useMemo(() => {
        if (viewMode !== 'table' || statusFilter === 'All') return filteredAndSortedJobs;
        // techinv-8c3f5a21-b — Needs Invoicing serves the org-wide set, so the rows
        // always equal the number on the pill and a stale uninvoiced job cannot hide
        // behind the date range. Every other pill is unchanged.
        if (statusFilter === 'NeedsInvoicing') return needsInvoicingJobs;
        return filteredAndSortedJobs.filter(j => j.status === statusFilter);
    }, [filteredAndSortedJobs, needsInvoicingJobs, statusFilter, viewMode]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleSelectJob = (jobId, selected) => {
        setSelectedJobIds(prev => {
            const next = new Set(prev);
            if (selected) next.add(jobId); else next.delete(jobId);
            return next;
        });
    };

    const handleSelectAll = (ids) => setSelectedJobIds(new Set(ids));

    // Execute the actual Firestore writes — shared by normal and override paths
    // chklstweb-3a7f1c58 — checklist gate first: a job whose required checklist isn't
    // finished is SKIPPED, not attempted. Writes are settled one by one, so one refusal
    // no longer hides which jobs failed. Skipped and failed jobs stay selected.
    const executeBatchComplete = async (jobIds, overrideMultiCrew = false) => {
        if (!jobIds.length) return;
        setBatchLoading(true);
        const orgId = currentUserProfile.organizationId;
        let keepSelected = [];
        try {
            const gates = await Promise.all(jobIds.map(id => readChecklistGate(orgId, id).catch(() => 'clear')));
            const blockedIds = jobIds.filter((id, i) => gates[i] === 'blocked');
            const clearIds = jobIds.filter((id, i) => gates[i] !== 'blocked');
            const labelFor = (id) => {
                const j = jobs.find(x => x.id === id);
                return `${formatJobNumber(j?.jobNumber)} ${j?.title || ''}`.trim() || 'A job';
            };
            const skipped = blockedIds.map(id => ({ id, label: labelFor(id), checklist: true, reason: "a required checklist isn't finished" }));
            const settled = await Promise.allSettled(clearIds.map(jobId => {
                const job = jobs.find(j => j.id === jobId);
                const isMultiCrew = (job?.crewsTotal || 0) > 1;
                const updatePayload = {
                    status: 'Completed',
                    completedAt: new Date(),
                    updatedAt: new Date(),
                };
                if (isMultiCrew && overrideMultiCrew) {
                    // Manager override — mark all crew entries complete
                    updatePayload.crewsCompleted = job.crewsTotal;
                    updatePayload.crews = (job.crews || []).map(crew => ({
                        ...crew,
                        completedAt: crew.completedAt || new Date(),
                        completedBy: crew.completedBy || currentUserProfile.id,
                        managerOverride: !crew.completedAt,
                    }));
                }
                return updateDoc(doc(db, `organizations/${orgId}/jobs`, jobId), updatePayload);
            }));
            for (let i = 0; i < settled.length; i++) {
                if (settled[i].status !== 'rejected') continue;
                const id = clearIds[i];
                const err = settled[i].reason;
                const msg = await explainChecklistRefusal({ orgId, jobId: id, error: err });
                skipped.push({ id, label: labelFor(id), checklist: !!msg, reason: msg ? "a required checklist isn't finished" : (err?.message || 'it could not be saved') });
            }
            keepSelected = skipped.map(s => s.id);
            setBatchResults({ type: 'complete', count: clearIds.length - (skipped.length - blockedIds.length), skipped });
        } catch (err) {
            setBatchResults({ type: 'error', message: err.message });
        } finally {
            setBatchLoading(false);
            setBatchCompleteModal(false);
            setMultiCrewWarning(null);
            setSelectedJobIds(new Set(keepSelected));
        }
    };

    const handleBatchComplete = async () => {
        if (!selectedJobIds.size) return;
        const toComplete = [...selectedJobIds].filter(id => {
            const job = jobs.find(j => j.id === id);
            return job && !['Canceled', 'Completed'].includes(job.status);
        });

        // Scan for multi-crew jobs with incomplete crew portions
        const multiCrewIncomplete = toComplete.filter(id => {
            const job = jobs.find(j => j.id === id);
            return job && (job.crewsTotal || 0) > 1 && (job.crewsCompleted || 0) < (job.crewsTotal || 0);
        });

        if (multiCrewIncomplete.length > 0) {
            // Build warning data — list affected jobs and their pending crews
            const affected = multiCrewIncomplete.map(id => {
                const job = jobs.find(j => j.id === id);
                const pendingCrews = (job.crews || [])
                    .filter(c => !c.completedAt)
                    .map(c => c.crewGroupName || 'Unknown crew');
                return { job, pendingCrews };
            });
            const cleanIds = toComplete.filter(id => !multiCrewIncomplete.includes(id));
            setBatchCompleteModal(false);
            setMultiCrewWarning({ affected, cleanIds });
            return;
        }

        // No multi-crew issues — execute immediately
        await executeBatchComplete(toComplete, false);
    };

    // r1-3f-jmv-open-1f7e5c93 — fetch customer-facing notes for the selected jobs,
    // then open the invoice modal with the picker. On fetch failure we still open
    // the modal but flag noteFetchFailed so the picker is hidden and we send NO
    // noteInclusions — degrading toward including everything (today's behaviour).
    const openBatchInvoiceModal = async () => {
        if (!selectedJobIds.size) return;
        setExcludedNoteIds(new Set());
        setNoteFetchFailed(false);
        setNoteGroups([]);
        setBatchInvoiceModal(true);
        try {
            const orgId = currentUserProfile.organizationId;
            const selectedJobs = jobs.filter(j => selectedJobIds.has(j.id));
            setNoteGroups(await fetchCustomerNoteGroups(db, orgId, selectedJobs));
        } catch (err) {
            console.error('Note inclusion fetch failed; including all notes as today.', err);
            setNoteFetchFailed(true);
        }
    };

    const toggleNoteExcluded = (id) => setExcludedNoteIds(prev => {
        const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    });
    // legnotes9-6b4e8d12 — SEAM 9. toggleLegacyExcluded / excludedLegacyJobIds removed
    // with the picker's "Older job notes from this job" row.

    const handleBatchCreateInvoices = async () => {
        if (!selectedJobIds.size) return;
        setBatchLoading(true);
        try {
            const functions = getFunctions();
            const batchCreate = httpsCallable(functions, 'batchCreateInvoices');
            // r1-3f-jmv-send-9a2d6b4e — send noteInclusions unless the fetch failed
            // (then omit → backend defaults to including everything).
            const noteInclusions = noteFetchFailed
                ? undefined
                : buildNoteInclusions(noteGroups, excludedNoteIds);
            const result = await batchCreate({ jobIds: [...selectedJobIds], sendImmediately, noteInclusions });
            setBatchResults({ type: 'invoice', ...result.data });
        } catch (err) {
            setBatchResults({ type: 'error', message: err.message });
        } finally {
            setBatchLoading(false);
            setBatchInvoiceModal(false);
            setSelectedJobIds(new Set());
            setBatchMode(false);
        }
    };

    const handleDeleteRequest = (jobId) => {
        setJobToDelete(jobId);
    };

    const handleConfirmDelete = async () => {
        if (!jobToDelete || !currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        const jobDocRef = doc(db, `organizations/${orgId}/jobs`, jobToDelete);

        try {
            await deleteDoc(jobDocRef);
        } catch (error) {
            console.error("Error removing job: ", error);
        } finally {
            setJobToDelete(null);
        }
    };

    const handleStatusUpdate = async (jobId, newStatus) => {
        if (!jobId || !newStatus || !currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        const jobRef = doc(db, `organizations/${orgId}/jobs`, jobId);
        try {
            await updateDoc(jobRef, { status: newStatus });
        } catch (error) {
            console.error("Failed to update job status:", error);
        }
    };

    const handleKanbanJobInteraction = (job, overrideStatus = null) => {
        if (job && onRequestJobStart) {
            onRequestJobStart(job, overrideStatus);
        } else if (job) {
            openJobEditorModal(job);
        }
    };

    const clearFilter = () => {
        navigate('/jobs');
        setSelectedPreset('thisweek');
    };

    // Reset the Jobs view to defaults and drop the persisted prefs (marker 4e81b0c2).
    // Leaves the deep-link clearFilter, search X, and batch Cancel behaviours alone.
    const resetView = () => {
        setViewMode('board');
        setSortConfig({ key: 'startAt', direction: 'descending' });
        setStatusFilter('All');
        setSelectedPreset('thisweek');
        setSearchTerm('');
        try { localStorage.removeItem(JOBS_PREFS_KEY_PREFIX + currentUserProfile.id); } catch {}
    };

    return (
        <>
            {/* FIX: Added w-full min-w-0 to prevent horizontal overflow */}
            <div className="p-4 sm:p-6 md:p-10 w-full min-w-0">
                {/* FIX: Changed to flex-col lg:flex-row with w-full to allow proper stacking on mobile */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 w-full">
                    <div className="w-full lg:w-auto">
                        <h1 className="text-3xl font-bold text-gray-800">Jobs</h1>
                        {filterParam === 'RequiresInvoicing' && (
                            <div className="flex items-center gap-2 mt-2">
                                <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded border border-green-200">
                                    Filter: Requires Invoicing
                                </span>
                                <button onClick={clearFilter} className="text-gray-400 hover:text-red-500 transition-colors">
                                    <XCircle size={16} />
                                </button>
                            </div>
                        )}
                        {filterParam === 'NeedsConfirmation' && (
                            <div className="flex items-center gap-2 mt-2">
                                <span className="bg-red-100 text-red-800 text-xs font-semibold px-2.5 py-0.5 rounded border border-red-200">
                                    📞 Needs Confirmation — call each customer and move to Scheduled when confirmed
                                </span>
                                <button onClick={clearFilter} className="text-gray-400 hover:text-red-500 transition-colors">
                                    <XCircle size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* FIX: Replaced space-x-2 with flex-wrap gap-2 sm:gap-3, made full width on mobile */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto">
                        
                        {!filterParam && (
                            <>
                                <div className="flex items-center border rounded-md p-1 bg-white shadow-sm flex-1 sm:flex-none min-w-[130px]">
                                    <select
                                        value={selectedPreset}
                                        onChange={(e) => setSelectedPreset(e.target.value)}
                                        className="p-1 border-0 rounded-md text-sm font-medium text-gray-600 focus:ring-0 focus:outline-none bg-transparent appearance-none pr-8 w-full" 
                                        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")', backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }} 
                                    >
                                        {datePresets.map(preset => (
                                            <option key={preset.value} value={preset.value}>
                                                {preset.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedPreset === 'custom' && (
                                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 border rounded-md p-1 bg-white shadow-sm w-full sm:w-auto">
                                         <label htmlFor="start-date" className="text-sm font-medium text-gray-600 pl-1">From:</label>
                                         <input
                                             type="date" id="start-date" value={startDate || ''}
                                             onChange={(e) => setStartDate(e.target.value)}
                                             className="p-1 border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                         />
                                         <label htmlFor="end-date" className="text-sm font-medium text-gray-600 pl-1">To:</label>
                                         <input
                                             type="date" id="end-date" value={endDate || ''}
                                             onChange={(e) => setEndDate(e.target.value)}
                                             className="p-1 border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                         />
                                    </div>
                                )}

                                {/* Reset the view to defaults + clear saved prefs (marker 4e81b0c2) */}
                                <button
                                    onClick={resetView}
                                    title="Reset view to defaults"
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 bg-white border shadow-sm hover:bg-gray-50 transition-colors"
                                >
                                    <RotateCcw size={15} /> Reset view
                                </button>
                            </>
                        )}

                        <div className="relative flex-1 sm:flex-none min-w-[150px]">
                            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search jobs..."
                                className="pl-8 pr-3 py-1.5 border rounded-md text-sm text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-44"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <XCircle size={14} />
                                </button>
                            )}
                        </div>

                        {/* FIX: Grouped toggles and buttons together, added flex-1 to button for stretching on mobile */}
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <button onClick={() => handleSetViewMode('dispatch')} title="Today's Dispatch"
                                className={`p-2 rounded-md transition-colors ${viewMode === 'dispatch' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>
                                <Radio size={20} />
                            </button>
                            <button onClick={() => handleSetViewMode('board')} title="Kanban Board"
                                className={`p-2 rounded-md transition-colors ${viewMode === 'board' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>
                                <LayoutGrid size={20} />
                            </button>
                            <button onClick={() => handleSetViewMode('table')} title="List View"
                                className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>
                                <List size={20} />
                            </button>

                            {(can(currentUserProfile, PERMISSIONS.MANAGE_JOBS) || techJobCreationEnabled) && (
                                <button className="flex-1 sm:flex-none flex items-center justify-center p-2 sm:p-3 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap" onClick={() => openJobEditorModal()}>
                                    <Plus size={20} className="mr-1 sm:mr-2" /> Add Job
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {isLoading ? (
                    <p className="text-center mt-8">Loading jobs...</p>
                ) : viewMode === 'dispatch' ? (
                    <DispatchView
                        jobs={filteredAndSortedJobs}
                        currentUserProfile={currentUserProfile}
                        onRequestJobStart={onRequestJobStart}
                    />
                ) : viewMode === 'table' ? (
                    <>
                        {/* Status filter + batch toolbar */}
                        <div className="flex flex-wrap gap-2 mb-3 items-center justify-between">
                            <div className="flex flex-wrap gap-1.5 items-center">
                                {/* techinv-8c3f5a21-b — uses VALID_JOBS_STATUS_FILTERS rather than
                                    re-listing the same six strings inline, so the pills and the
                                    saved-preference validator cannot drift apart.
                                    Needs Invoicing carries its count so the office can see at a
                                    glance whether anything is waiting, instead of having to click
                                    the pill to find out. Hidden at zero — "Needs Invoicing (0)"
                                    is noise, matching the mobile attention card's `> 0` habit. */}
                                {VALID_JOBS_STATUS_FILTERS.map(s => (
                                    <button key={s} onClick={() => setStatusFilter(s)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                                            ${statusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                        {s === 'NeedsInvoicing'
                                            ? (needsInvoicingJobs.length > 0 ? `Needs Invoicing (${needsInvoicingJobs.length})` : 'Needs Invoicing')
                                            : s}
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                {batchMode && selectedJobIds.size > 0 && (
                                    <>
                                        {hasCompletableSelected && (
                                        <button onClick={() => setBatchCompleteModal(true)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700">
                                            <CheckCircle size={14} /> Complete {selectedJobIds.size}
                                        </button>
                                        )}
                                        {canBatchInvoice(currentUserProfile) && ( /* s8portal-4e7b2c95 — server: batchCreateInvoices gate */
                                        <button onClick={openBatchInvoiceModal}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700">
                                            <FileText size={14} /> Invoice {selectedJobIds.size}
                                        </button>
                                        )}
                                    </>
                                )}
                                <button onClick={() => { setBatchMode(m => !m); setSelectedJobIds(new Set()); }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors
                                        ${batchMode ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                                    <CheckSquare size={14} /> {batchMode ? 'Cancel' : 'Select'}
                                </button>
                            </div>
                        </div>
                        <JobTable
                            jobs={statusFilteredJobs}
                            // PHASE FU-QV VERIFIER: r1-fu-qv-2e7b4f81
                            // Route row clicks through onRequestJobStart so the
                            // status-aware routing (QuickView for Scheduled/In
                            // Progress, full editor otherwise) applies in the
                            // List view too. Pre-fix: every list-row click went
                            // straight to JobFormModal, skipping the QuickView
                            // surface that a tech needs to start an active job.
                            // Fallback to openJobEditorModal() with no args is
                            // preserved for any "+ Add" path inside JobTable
                            // that calls openModal without a job argument.
                            openModal={(job) => job ? onRequestJobStart(job) : openJobEditorModal()}
                            onDeleteRequest={handleDeleteRequest}
                            requestSort={requestSort}
                            sortConfig={sortConfig}
                            batchMode={batchMode}
                            selectedJobIds={selectedJobIds}
                            onSelectJob={handleSelectJob}
                            onSelectAll={handleSelectAll}
                        />
                    </>
                ) : (
                    <JobKanbanBoard
                        jobs={filteredAndSortedJobs}
                        statuses={jobStatuses}
                        onCardClick={handleKanbanJobInteraction}
                        resources={resources}
                    />
                )}
            </div>
            <ConfirmationModal
                isOpen={!!jobToDelete}
                onClose={() => setJobToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete Job"
                message="Are you sure you want to permanently delete this job? This action cannot be undone."
                confirmText="Yes, Delete"
            />

            {/* MULTI-CREW WARNING MODAL */}
            {multiCrewWarning && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Users size={18} className="text-amber-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">Multi-crew jobs detected</h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {multiCrewWarning.affected.length} job{multiCrewWarning.affected.length !== 1 ? 's have' : ' has'} crew portions that haven't been marked complete yet.
                                </p>
                            </div>
                        </div>

                        {/* Affected jobs list */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 space-y-2 max-h-48 overflow-y-auto">
                            {multiCrewWarning.affected.map(({ job, pendingCrews }) => (
                                <div key={job.id} className="flex items-start justify-between gap-2">
                                    <p className="text-sm font-medium text-gray-800 truncate">{job.title} — {job.clientName}</p>
                                    <div className="flex flex-wrap gap-1 flex-shrink-0">
                                        {pendingCrews.map(crew => (
                                            <span key={crew} className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded-full whitespace-nowrap">
                                                {crew} pending
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Action buttons */}
                        <div className="space-y-2">
                            {can(currentUserProfile, PERMISSIONS.MANAGE_JOBS) && (
                                <button
                                    onClick={() => executeBatchComplete(
                                        [...multiCrewWarning.affected.map(a => a.job.id), ...multiCrewWarning.cleanIds],
                                        true
                                    )}
                                    disabled={batchLoading}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                                >
                                    {batchLoading
                                        ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        : <CheckCircle size={15} />
                                    }
                                    Close all crews &amp; complete
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    if (multiCrewWarning.cleanIds.length > 0) {
                                        executeBatchComplete(multiCrewWarning.cleanIds, false);
                                    } else {
                                        setMultiCrewWarning(null);
                                    }
                                }}
                                disabled={batchLoading}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                {multiCrewWarning.cleanIds.length > 0
                                    ? `Skip these ${multiCrewWarning.affected.length} — complete remaining ${multiCrewWarning.cleanIds.length}`
                                    : 'Skip these jobs'
                                }
                            </button>
                            <button
                                onClick={() => setMultiCrewWarning(null)}
                                disabled={batchLoading}
                                className="w-full px-4 py-2.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BATCH COMPLETE MODAL */}
            {batchCompleteModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-2">Mark {selectedJobIds.size} Job{selectedJobIds.size !== 1 ? 's' : ''} Complete?</h2>
                        <p className="text-sm text-gray-500 mb-6">This will mark all selected jobs as <strong>Completed</strong>. Already completed or canceled jobs will be skipped.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setBatchCompleteModal(false)} disabled={batchLoading}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                            <button onClick={handleBatchComplete} disabled={batchLoading}
                                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2">
                                {batchLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CheckCircle size={15} />}
                                {batchLoading ? 'Updating...' : 'Mark Complete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BATCH CREATE INVOICE MODAL */}
            {batchInvoiceModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-2">Create {selectedJobIds.size} Invoice{selectedJobIds.size !== 1 ? 's' : ''}?</h2>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                            <p className="text-sm text-amber-800 font-medium">⚠️ Have you confirmed all line items are complete?</p>
                            <p className="text-xs text-amber-600 mt-1">Invoices will be created as-is from each job's current line items.</p>
                        </div>
                        {/* r1-3f-jmv-render-3b8e1d40 — note inclusion picker. Hidden when the
                            fetch failed or no selected job has customer-facing notes; an early
                            Create click before the fetch resolves sends {} → include everything. */}
                        {!noteFetchFailed && noteGroups.length > 0 && (
                            <div className="mb-4">
                                <p className="text-sm font-medium text-gray-700 mb-2">Notes to include on these invoices</p>
                                <NoteInclusionPicker
                                    groups={noteGroups}
                                    excludedNoteIds={excludedNoteIds}
                                    onToggleNote={toggleNoteExcluded}
                                />
                            </div>
                        )}
                        <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg border">
                            <input type="checkbox" id="sendNow" checked={sendImmediately} onChange={e => setSendImmediately(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer" />
                            <label htmlFor="sendNow" className="text-sm text-gray-700 cursor-pointer">
                                <span className="font-medium">Send to customers immediately</span>
                                <span className="block text-xs text-gray-500">Each customer receives an email with a payment link</span>
                            </label>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setBatchInvoiceModal(false)} disabled={batchLoading}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                            <button onClick={handleBatchCreateInvoices} disabled={batchLoading}
                                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2">
                                {batchLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FileText size={15} />}
                                {batchLoading ? 'Creating...' : `Create${sendImmediately ? ' & Send' : ''}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* BATCH RESULTS MODAL */}
            {batchResults && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                        <h2 className="text-lg font-bold text-gray-800 mb-4">
                            {batchResults.type === 'complete' ? 'Jobs Updated' : batchResults.type === 'error' ? 'Error' : 'Invoices Created'}
                        </h2>
                        {batchResults.type === 'complete' && (
                            <div className="bg-green-50 rounded-lg p-4 mb-4">
                                <p className="text-green-700 font-medium">{batchResults.count} job{batchResults.count !== 1 ? 's' : ''} marked as Completed</p>
                            </div>
                        )}
                        {/* chklstweb-3a7f1c58 — jobs left out, and why. They stay selected. */}
                        {batchResults.type === 'complete' && batchResults.skipped?.length > 0 && (
                            <div className="bg-red-50 rounded-lg p-3 mb-4">
                                <p className="text-red-700 font-medium mb-1">{batchResults.skipped.length} not completed — still selected:</p>
                                {batchResults.skipped.map((s, i) => (
                                    <p key={i} className="text-xs text-red-600">• {s.label} — {s.reason}</p>
                                ))}
                                {batchResults.skipped.some(s => s.checklist) && (
                                    <p className="text-xs text-red-500 mt-2">Open a job to finish its checklist. An Owner, Admin or Manager can complete it anyway from the job.</p>
                                )}
                            </div>
                        )}
                        {batchResults.type === 'invoice' && (
                            <div className="space-y-3 mb-4">
                                <div className="bg-green-50 rounded-lg p-3">
                                    <p className="text-green-700 font-medium">{batchResults.created?.length || 0} invoice{batchResults.created?.length !== 1 ? 's' : ''} created</p>
                                </div>
                                {batchResults.sent?.length > 0 && (
                                    <div className="bg-blue-50 rounded-lg p-3">
                                        <p className="text-blue-700 font-medium">{batchResults.sent.length} sent to customers</p>
                                    </div>
                                )}
                                {batchResults.failed?.length > 0 && (
                                    <div className="bg-red-50 rounded-lg p-3">
                                        <p className="text-red-700 font-medium mb-1">{batchResults.failed.length} failed:</p>
                                        {batchResults.failed.map((f, i) => (
                                            <p key={i} className="text-xs text-red-500">• {f.reason}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {batchResults.type === 'error' && (
                            <div className="bg-red-50 rounded-lg p-4 mb-4">
                                <p className="text-red-700">{batchResults.message}</p>
                            </div>
                        )}
                        <button onClick={() => setBatchResults(null)}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">Done</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default JobManagementView;